import React from 'react'
import PropTypes from 'prop-types'

const whitePitchClasses = new Set([0, 2, 4, 5, 7, 9, 11])

// key fills stay fixed white/black in both themes (not the shared auto-invert filter) - only the black key's outline follows theme
const noteColor = '#0BB79C'
const bassRingColor = '#ffc107'
const whiteKeyColor = '#ffffff'
const blackKeyColor = '#1a1a1a'
const keyBorderColor = '#9e9e9e'
const themeTextColor = 'var(--main-title-color)'

// 2:1 width:height so the diagram reads as a wide, short keyboard strip.
const viewWidth = 140
const viewHeight = 70

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const buildSlots = (windowStartPitchClass, semitoneSpan) => {
  const slots = []
  let whiteKeysSoFar = 0
  for (let o = 0; o < semitoneSpan; o++) {
    const absolutePitchClass = (windowStartPitchClass + o) % 12
    const isWhite = whitePitchClasses.has(absolutePitchClass)
    slots.push({ localOffset: o, isWhite, columnIndex: whiteKeysSoFar })
    if (isWhite) whiteKeysSoFar++
  }
  return slots
}

const Piano = ({ voicing }) => {
  if (!voicing) return null

  const { noteOffsets, bassPitchClass, windowStartPitchClass, semitoneSpan } = voicing
  const slots = buildSlots(windowStartPitchClass, semitoneSpan)
  const whiteSlots = slots.filter(s => s.isWhite)
  const blackSlots = slots.filter(s => !s.isWhite)
  const whiteKeyCount = whiteSlots.length
  if (whiteKeyCount === 0) return null

  // reserves the margin the removed key labels used to occupy
  const keyAreaHeight = viewHeight - 10
  const whiteKeyWidth = viewWidth / whiteKeyCount
  const whiteKeyGap = whiteKeyWidth * 0.14
  const whiteKeyDrawnWidth = whiteKeyWidth - whiteKeyGap
  const blackKeyHeight = keyAreaHeight * 0.6
  const blackKeyWidth = whiteKeyWidth * 0.58
  const whiteKeyRadius = clamp(whiteKeyDrawnWidth * 0.175, 1.5, 4)
  const blackKeyRadius = clamp(blackKeyWidth * 0.19, 1, 2.5)
  const dotRadius = whiteKeyWidth * 0.3

  const isNote = localOffset => noteOffsets.has(localOffset)
  const absolutePitchClassOf = localOffset => (windowStartPitchClass + localOffset) % 12

  return <svg
    width='100%'
    xmlns='http://www.w3.org/2000/svg'
    preserveAspectRatio='xMinYMin meet'
    viewBox={`0 0 ${viewWidth} ${viewHeight}`}>
    <g>
      {whiteSlots.map(slot => {
        const x = slot.columnIndex * whiteKeyWidth
        const absolutePitchClass = absolutePitchClassOf(slot.localOffset)
        const noteHere = isNote(slot.localOffset)

        return <g key={'white-' + slot.localOffset}>
          <rect
            x={x} y={0} width={whiteKeyDrawnWidth} height={keyAreaHeight}
            rx={whiteKeyRadius} ry={whiteKeyRadius}
            fill={whiteKeyColor}
            stroke={keyBorderColor}
            strokeWidth='0.4' />
          {noteHere && <g>
            <circle
              cx={x + whiteKeyDrawnWidth / 2}
              cy={keyAreaHeight * 0.74}
              r={dotRadius}
              fill={noteColor} />
            {bassPitchClass !== null && bassPitchClass !== undefined && absolutePitchClass === bassPitchClass &&
              <circle
                cx={x + whiteKeyDrawnWidth / 2}
                cy={keyAreaHeight * 0.74}
                r={dotRadius + 0.8}
                fill='none'
                stroke={bassRingColor}
                strokeWidth='0.8' />}
          </g>}
        </g>
      })}

      {blackSlots.map(slot => {
        const x = slot.columnIndex * whiteKeyWidth - blackKeyWidth / 2
        const absolutePitchClass = absolutePitchClassOf(slot.localOffset)
        const noteHere = isNote(slot.localOffset)

        return <g key={'black-' + slot.localOffset}>
          <rect
            x={x} y={0} width={blackKeyWidth} height={blackKeyHeight}
            rx={blackKeyRadius} ry={blackKeyRadius}
            fill={blackKeyColor}
            stroke={themeTextColor}
            strokeWidth='0.8' />
          {noteHere && <g>
            <circle
              cx={x + blackKeyWidth / 2}
              cy={blackKeyHeight * 0.55}
              r={dotRadius}
              fill={noteColor} />
            {bassPitchClass !== null && bassPitchClass !== undefined && absolutePitchClass === bassPitchClass &&
              <circle
                cx={x + blackKeyWidth / 2}
                cy={blackKeyHeight * 0.55}
                r={dotRadius + 0.8}
                fill='none'
                stroke={bassRingColor}
                strokeWidth='0.8' />}
          </g>}
        </g>
      })}
    </g>
  </svg>
}

Piano.propTypes = {
  voicing: PropTypes.shape({
    noteOffsets: PropTypes.object.isRequired, // Set<number>
    rootPitchClass: PropTypes.number.isRequired,
    bassPitchClass: PropTypes.number,
    windowStartPitchClass: PropTypes.number.isRequired,
    semitoneSpan: PropTypes.number.isRequired,
  }),
}

export default Piano
